<template>
  <v-container>
    <!-- ✅ Loading bar -->
    <v-progress-linear
      :active="loading"
      :indeterminate="loading"
      color="primary"
      height="3"
      class="mb-3"
    />

    <div class="d-flex align-center mb-3">
      <v-spacer />
      <v-btn
        color="primary"
        depressed
        :loading="creating"
        :disabled="loading || creating"
        @click="createDialog = true"
      >
        <v-icon left>mdi-plus</v-icon>{{ $t("common.create") }}
      </v-btn>
    </div>

    <v-row dense>
      <v-col cols="12" md="6" class="py-1" v-for="g in groupsUI" :key="g.id">
        <GroupCard
          :group="g"
          :previewLimit="previewLimit"
          :canDelete="!!canDeleteMap[g.id]"
          @invite="openInvite"
          @delete="onDeleteGroup"
          @leave="onLeaveGroup"
        />
      </v-col>
    </v-row>

    <CreateGroupDialog v-model="createDialog" @create="createGroup" />
    <InviteDialog v-model="inviteDialog" :link="inviteLink" />

    <v-dialog v-model="confirmDialog.open" max-width="460">
      <v-card>
        <v-card-text class="pt-6" style="white-space: pre-line;">
          {{ confirmDialog.message }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeConfirmDialog">{{ $t("common.no") }}</v-btn>
          <v-btn :color="confirmDialog.actionColor" depressed @click="runConfirmedAction">
            {{ $t("common.yes") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import GroupCard from "../components/Groups/GroupCard.vue";
import CreateGroupDialog from "../components/Groups/CreateGroupDialog.vue";
import InviteDialog from "../components/Groups/InviteDialog.vue";

import { listMyGroupsWithMembers, deleteGroup } from "@/services/db";

export default {
  components: { GroupCard, CreateGroupDialog, InviteDialog },
  data: () => ({
    createDialog: false,
    inviteDialog: false,
    inviteLink: "",
    groupsUI: [],
    previewLimit: 6,
    canDeleteMap: {},
    confirmDialog: {
      open: false,
      mode: null,
      groupId: null,
      message: "",
      actionColor: "error"
    },

    // ✅ estados de carga
    loading: false,
    creating: false
  }),
  computed: {
    ...mapGetters("groups", { groups: "groups" }),
    uid() {
      return this.$store.getters["auth/uid"];
    }
  },
  async created() {
    this.loading = true;
    try {
      await Promise.allSettled([
        this.$store.dispatch("groups/refresh"),
        this.refreshGroupsUI()
      ]);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async refreshGroupsUI() {
      if (!this.uid) return;

      this.loading = true;
      try {
        // 1) grupos + miembros
        const list = await listMyGroupsWithMembers(this.uid);
        this.groupsUI = list;

        // 2) permisos locales: owner o admin dentro del grupo cargado
        const map = {};
        for (const g of list) {
          const isOwner = g?.ownerId === this.uid;
          const myMembership = (g.members || []).find((m) => m.userId === this.uid);
          map[g.id] = isOwner || myMembership?.role === "admin";
        }
        this.canDeleteMap = map;
      } finally {
        this.loading = false;
      }
    },

    async createGroup({ name, type }) {
      this.creating = true;
      this.loading = true;
      try {
        await this.$store.dispatch("groups/create", { name, type });
        await Promise.allSettled([
          this.$store.dispatch("groups/refresh"),
          this.refreshGroupsUI()
        ]);
      } finally {
        this.creating = false;
        this.loading = false;
      }
    },

    async openInvite(groupId) {
      const link = await this.$store.dispatch("groups/inviteLink", { groupId });
      this.inviteLink = link;

      if (await this.shareInviteLink(link)) return;

      this.inviteDialog = true;
    },

    async shareInviteLink(link) {
      if (!navigator.share) return false;

      try {
        await navigator.share({
          title: this.$t("groups.webShareTitle"),
          text: this.$t("groups.webShareText"),
          url: link
        });
        return true;
      } catch (e) {
        return false;
      }
    },

    openConfirmDialog({ mode, groupId, message, actionColor }) {
      this.confirmDialog = {
        open: true,
        mode,
        groupId,
        message,
        actionColor
      };
    },

    closeConfirmDialog() {
      this.confirmDialog = {
        open: false,
        mode: null,
        groupId: null,
        message: "",
        actionColor: "error"
      };
    },

    onDeleteGroup(groupId) {
      this.openConfirmDialog({
        mode: "delete",
        groupId,
        message: this.$t("groups.deleteGroupConfirm"),
        actionColor: "error"
      });
    },

    onLeaveGroup(groupId) {
      this.openConfirmDialog({
        mode: "leave",
        groupId,
        message: this.$t("groups.leaveGroupConfirm"),
        actionColor: "warning"
      });
    },

    async runConfirmedAction() {
      const { mode, groupId } = this.confirmDialog;
      if (!mode || !groupId) {
        this.closeConfirmDialog();
        return;
      }

      this.loading = true;
      try {
        if (mode === "delete") {
          await deleteGroup({ groupId, uid: this.uid });
        } else if (mode === "leave") {
          await this.$store.dispatch("groups/leave", { groupId, uid: this.uid });
        }
        await Promise.allSettled([
          this.$store.dispatch("groups/refresh"),
          this.refreshGroupsUI()
        ]);
      } catch (e) {
        const fallback =
          mode === "delete"
            ? this.$t("groups.deleteGroupError")
            : this.$t("groups.leaveGroupError");
        alert(e?.message || fallback);
      } finally {
        this.loading = false;
        this.closeConfirmDialog();
      }
    }
  }
};
</script>
